"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import type { ArticleReferenceBlock as ArticleReferenceBlockData, Media, Post } from "@/payload-types";
import { cn } from "@/utilities/core/cn";
import { getMediaUrl } from "@/utilities/media/getMediaUrl";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { formatRelativeTime } from "@/utilities/formatting/date";

interface ArticleReferenceBlockProps {
    block: ArticleReferenceBlockData;
    className?: string;
}

export function ArticleReferenceBlock({ block, className }: ArticleReferenceBlockProps) {
    const urlBuilder = useNavigation();
    const post = typeof block.post === "object" ? (block.post as Post) : null;

    if (!post?.slug || !post?.title) {
        return null;
    }

    const hero = (post.heroImage as Media | null | undefined) ?? null;
    const heroUrl = hero?.url ? getMediaUrl(hero, "small") || hero.url : null;
    const href = urlBuilder.post(post);
    const summary = typeof post.summary === "string" ? post.summary : null;
    const relativeTime = formatRelativeTime(post.publishedAt);

    return (
        <Link
            prefetch={false}
            href={href}
            className={cn(
                "not-prose group mb-6 flex gap-4 rounded-lg border border-border bg-card p-3 no-underline transition-colors hover:bg-muted/50",
                className,
            )}
        >
            {heroUrl && (
                <div className="relative aspect-video w-32 shrink-0 overflow-hidden rounded-md sm:w-40">
                    <Image
                        src={heroUrl}
                        alt={hero?.alt || post.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 640px) 128px, 160px"
                        unoptimized
                    />
                </div>
            )}
            <div className="flex min-w-0 flex-col justify-center">
                <h3 className="m-0 line-clamp-2 text-base font-semibold text-foreground group-hover:text-primary">
                    {post.title}
                </h3>
                {summary && <p className="m-0 mt-1 line-clamp-2 text-sm text-muted-foreground">{summary}</p>}
                {relativeTime && (
                    <span className="mt-1 text-xs text-muted-foreground/80">{relativeTime}</span>
                )}
            </div>
        </Link>
    );
}
